import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChartBarIcon,
  UsersIcon,
  VideoCameraIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
  UserCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { signOutUser, getClassSettings } from '../../services/firebaseService';

export interface AdminHeaderProps {
  activeNav?: 'dashboard' | 'applications' | 'attendance' | 'queries' | 'exams' | 'settings';
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  onExportExcel?: () => void;
  isLiveNow?: boolean;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({
  activeNav,
  title,
  subtitle,
  actions,
  onExportExcel,
  isLiveNow: propIsLiveNow
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [adminUser, setAdminUser] = useState<any>(null);
  const [isLive, setIsLive] = useState<boolean>(propIsLiveNow ?? false);

  useEffect(() => {
    const stored = localStorage.getItem('adminUser');
    if (stored) {
      try {
        setAdminUser(JSON.parse(stored));
      } catch (e) {
        console.error('Error parsing adminUser:', e);
      }
    }

    if (propIsLiveNow === undefined) {
      getClassSettings()
        .then(settings => {
          if (settings && typeof settings.isLiveNow === 'boolean') {
            setIsLive(settings.isLiveNow);
          }
        })
        .catch(() => { });
    } else {
      setIsLive(propIsLiveNow);
    }
  }, [propIsLiveNow]);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setProfileDropdownOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await signOutUser();
    } catch (e) {
      console.warn('Firebase signout warning:', e);
    }
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const navItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: ChartBarIcon,
      path: '/admin/dashboard',
      badge: null
    },
    {
      id: 'applications',
      label: 'Registrations',
      icon: UsersIcon,
      path: '/admin/applications',
      badge: null
    },
    {
      id: 'attendance',
      label: 'Live Classes',
      icon: VideoCameraIcon,
      path: '/admin/attendance',
      badge: isLive ? 'LIVE' : null,
      badgeColor: 'bg-rose-500 text-white animate-pulse'
    },
    {
      id: 'queries',
      label: 'Weekly Queries',
      icon: QuestionMarkCircleIcon,
      path: '/admin/queries',
      badge: null
    },
    {
      id: 'exams',
      label: 'Phase 2 Exams',
      icon: AcademicCapIcon,
      path: '/admin/exams',
      badge: 'Portal',
      badgeColor: 'bg-amber-100 text-amber-800'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: CogIcon,
      path: '/admin/settings',
      badge: null
    }
  ];

  const currentTab = activeNav || (
    location.pathname.includes('/applications') ? 'applications' :
      location.pathname.includes('/attendance') || location.pathname.includes('/classes') ? 'attendance' :
        location.pathname.includes('/queries') ? 'queries' :
          location.pathname.includes('/exams') ? 'exams' :
            location.pathname.includes('/settings') ? 'settings' :
              'dashboard'
  );

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Administrator';
      case 'accounts': return 'Accounts Desk';
      case 'deo': return 'Data Entry';
      default: return 'Admin Portal';
    }
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-sm transition-all">
        {/* Main Header Container */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20 gap-2 sm:gap-4">
            {/* Left: Branding & Page Context */}
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center space-x-2.5 sm:space-x-3 text-left group focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-xl p-1 -m-1"
                title="Go to Admin Dashboard"
              >
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-tr from-primary-700 via-primary-600 to-indigo-500 flex items-center justify-center text-white shadow-md shadow-primary-500/20 group-hover:scale-105 transition-transform">
                  <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center space-x-1.5">
                    <span className="font-extrabold text-base sm:text-lg tracking-tight text-slate-900 group-hover:text-primary-600 transition-colors truncate">
                      Okreed Admin
                    </span>
                    <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-primary-100 text-primary-800">
                      v2.0
                    </span>
                  </div>
                  <p className="text-[11px] sm:text-xs text-slate-500 truncate font-medium">
                    {subtitle || 'Kreed Odisha Sports Management'}
                  </p>
                </div>
              </button>
            </div>

            {/* Middle: Desktop Navigation Pills */}
            <nav className="hidden lg:flex items-center space-x-1 xl:space-x-1.5 bg-slate-100/90 p-1.5 rounded-2xl border border-slate-200/60 shadow-inner">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.path)}
                    className={`relative flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${isActive
                        ? 'bg-white text-primary-700 shadow-sm shadow-slate-200/80 font-extrabold'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                      }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-primary-600' : 'text-slate-400'}`} />
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className={`px-1.5 py-0.2 rounded-full text-[9px] font-black tracking-wide ${item.badgeColor || 'bg-slate-200 text-slate-700'}`}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Right: Actions, User Profile & Mobile Toggle */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Optional page-level custom actions */}
              {actions && (
                <div className="hidden md:flex items-center space-x-2">
                  {actions}
                </div>
              )}

              {/* Direct Export Excel Button if available */}
              {onExportExcel && (
                <button
                  type="button"
                  onClick={onExportExcel}
                  className="hidden sm:inline-flex items-center space-x-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 px-3 py-1.5 sm:py-2 rounded-xl text-xs font-bold transition shadow-sm"
                  title="Export records to Excel"
                >
                  <ArrowDownTrayIcon className="w-4 h-4 text-emerald-600" />
                  <span className="hidden md:inline">Export</span>
                </button>
              )}

              {/* Live stream badge pill */}
              {isLive && (
                <button
                  onClick={() => navigate('/admin/attendance')}
                  className="hidden sm:inline-flex items-center space-x-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-2.5 py-1.5 rounded-xl text-xs font-bold transition animate-pulse"
                >
                  <span className="w-2 h-2 rounded-full bg-rose-600" />
                  <span>Class Live</span>
                </button>
              )}

              {/* Profile Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-2 p-1.5 sm:px-3 sm:py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200/80 border border-slate-200/60 transition focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary-600 text-white flex items-center justify-center font-bold text-xs uppercase shadow-sm">
                    {adminUser?.displayName?.[0] || adminUser?.email?.[0] || 'A'}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-xs font-bold text-slate-800 leading-tight truncate max-w-[110px]">
                      {adminUser?.displayName || adminUser?.email?.split('@')[0] || 'Admin'}
                    </p>
                    <p className="text-[10px] text-slate-500 font-semibold uppercase">
                      {getRoleLabel(adminUser?.role)}
                    </p>
                  </div>
                  <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400 hidden sm:block" />
                </button>

                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-white shadow-xl border border-slate-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="px-4 py-2.5 border-b border-slate-100">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Signed in as</p>
                      <p className="text-sm font-bold text-slate-900 truncate">{adminUser?.email || 'admin@okreed.com'}</p>
                      <span className="inline-flex mt-1 items-center px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">
                        {getRoleLabel(adminUser?.role)}
                      </span>
                    </div>

                    <div className="py-1">
                      <button
                        onClick={() => navigate('/admin/settings?tab=profile')}
                        className="w-full text-left px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                      >
                        <UserCircleIcon className="w-4 h-4 text-slate-400" />
                        <span>My Account Settings</span>
                      </button>
                      <button
                        onClick={() => navigate('/admin/settings')}
                        className="w-full text-left px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                      >
                        <CogIcon className="w-4 h-4 text-slate-400" />
                        <span>Command Center</span>
                      </button>
                    </div>

                    <div className="border-t border-slate-100 pt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 flex items-center space-x-2"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4 text-rose-500" />
                        <span>Sign Out of Portal</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Hamburger Menu Button */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
                aria-label="Toggle navigation menu"
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="w-6 h-6 text-slate-800" />
                ) : (
                  <Bars3Icon className="w-6 h-6 text-slate-800" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Slide-down Drawer Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white/98 backdrop-blur-xl px-4 pt-3 pb-6 shadow-2xl animate-in slide-in-from-top duration-200">
            {/* User Greeting on mobile */}
            <div className="flex items-center justify-between p-3 mb-3 bg-slate-50 rounded-2xl border border-slate-200/70">
              <div className="flex items-center space-x-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-primary-600 text-white flex items-center justify-center font-bold text-sm shadow-sm flex-shrink-0">
                  {adminUser?.displayName?.[0] || adminUser?.email?.[0] || 'A'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900 truncate">
                    {adminUser?.displayName || adminUser?.email?.split('@')[0] || 'Admin'}
                  </p>
                  <p className="text-[11px] text-slate-500 font-semibold truncate">
                    {adminUser?.email}
                  </p>
                </div>
              </div>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-primary-100 text-primary-800 border border-primary-200 flex-shrink-0">
                {adminUser?.role || 'admin'}
              </span>
            </div>

            {/* Mobile Navigation Links */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(item.path);
                    }}
                    className={`flex flex-col items-start p-3 rounded-2xl text-left border transition ${isActive
                        ? 'bg-primary-50/90 border-primary-300 text-primary-900 shadow-sm'
                        : 'bg-white border-slate-200/80 text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    <div className="flex items-center justify-between w-full mb-1">
                      <div className={`p-1.5 rounded-xl ${isActive ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      {item.badge && (
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black tracking-wide ${item.badgeColor || 'bg-slate-100 text-slate-600'}`}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-bold">{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Quick Actions in Mobile Drawer */}
            <div className="space-y-2 pt-2 border-t border-slate-200">
              {onExportExcel && (
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onExportExcel();
                  }}
                  className="w-full flex items-center justify-center space-x-2 py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-sm transition"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  <span>Download Excel Report</span>
                </button>
              )}

              {actions && (
                <div className="flex flex-col gap-2 pt-1">
                  {actions}
                </div>
              )}

              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center justify-center space-x-2 py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-rose-50 text-rose-600 font-bold text-xs border border-slate-200 hover:border-rose-200 transition"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                <span>Log Out</span>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Fixed Bottom Navigation Bar for Instant 1-Thumb Switching (Phones only) */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200/90 shadow-2xl px-2 py-1.5 pb-safe flex items-center justify-around">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className={`flex flex-col items-center py-1 px-2.5 rounded-xl transition ${currentTab === 'dashboard'
              ? 'text-primary-600 font-black scale-105'
              : 'text-slate-500 hover:text-slate-800 font-semibold'
            }`}
        >
          <ChartBarIcon className="w-5 h-5" />
          <span className="text-[10px] mt-0.5">Overview</span>
        </button>

        <button
          onClick={() => navigate('/admin/applications')}
          className={`flex flex-col items-center py-1 px-2.5 rounded-xl transition ${currentTab === 'applications'
              ? 'text-primary-600 font-black scale-105'
              : 'text-slate-500 hover:text-slate-800 font-semibold'
            }`}
        >
          <UsersIcon className="w-5 h-5" />
          <span className="text-[10px] mt-0.5">Applications</span>
        </button>

        <button
          onClick={() => navigate('/admin/attendance')}
          className={`flex flex-col items-center py-1 px-2.5 rounded-xl transition relative ${currentTab === 'attendance'
              ? 'text-rose-600 font-black scale-105'
              : 'text-slate-500 hover:text-slate-800 font-semibold'
            }`}
        >
          <VideoCameraIcon className="w-5 h-5" />
          <span className="text-[10px] mt-0.5">Classes</span>
          {isLive && (
            <span className="absolute top-1 right-2 w-2 h-2 rounded-full bg-rose-500 animate-ping" />
          )}
        </button>

        <button
          onClick={() => navigate('/admin/queries')}
          className={`flex flex-col items-center py-1 px-2.5 rounded-xl transition ${currentTab === 'queries'
              ? 'text-indigo-600 font-black scale-105'
              : 'text-slate-500 hover:text-slate-800 font-semibold'
            }`}
        >
          <QuestionMarkCircleIcon className="w-5 h-5" />
          <span className="text-[10px] mt-0.5">Queries</span>
        </button>

        <button
          onClick={() => navigate('/admin/settings')}
          className={`flex flex-col items-center py-1 px-2.5 rounded-xl transition ${currentTab === 'settings'
              ? 'text-primary-600 font-black scale-105'
              : 'text-slate-500 hover:text-slate-800 font-semibold'
            }`}
        >
          <CogIcon className="w-5 h-5" />
          <span className="text-[10px] mt-0.5">Settings</span>
        </button>
      </div>
    </>
  );
};

export default AdminHeader;
